import { useMemo, useState } from "react";
import { CheckCircle2, Edit3, FileCheck2, Files, Trash2 } from "lucide-react";
import { Iso2700127701ControlModificacionDeleteModal } from "../../../components/control-modificaciones/Iso2700127701ControlModificacionDeleteModal";
import { Iso2700127701ControlModificacionFormModal } from "../../../components/control-modificaciones/Iso2700127701ControlModificacionFormModal";
import { Iso2700127701ControlModificacionesTable } from "../../../components/control-modificaciones/Iso2700127701ControlModificacionesTable";
import {
  Iso2700127701Button,
  Iso2700127701ConfirmDialog,
  Iso2700127701DataTable,
  Iso2700127701Filters,
  Iso2700127701IconButton,
  Iso2700127701InputField,
  Iso2700127701Modal,
  Iso2700127701OverviewCard,
  Iso2700127701ScrollableCell,
  Iso2700127701SectionHeader,
  Iso2700127701SelectField,
  Iso2700127701Tabs,
  Iso2700127701TextareaField,
  type Iso2700127701TableColumn,
} from "../../../components/shared";
import { useIso2700127701DebouncedValue } from "../../../components/shared/useIso2700127701FilteredRequest";
import type {
  Iso2700127701ControlModificacion,
  Iso2700127701ControlModificacionPayload,
} from "../../../types/control-modificaciones";
import { createEmptyControlModificacionForm } from "../../../types/control-modificaciones";
import type {
  Iso2700127701DocumentoSistema,
  Iso2700127701DocumentoSistemaPayload,
} from "../../../types/iso2700127701";
import {
  createEmptyDocumentoSistemaForm,
  defaultDocumentosSistemaFilters,
} from "../../../types/iso2700127701";
import {
  createIso2700127701DocumentoControlModificacion,
  createIso2700127701DocumentoSistema,
  deleteIso2700127701DocumentoControlModificacion,
  deleteIso2700127701DocumentoSistema,
  updateIso2700127701DocumentoControlModificacion,
  updateIso2700127701DocumentoSistema,
} from "../api/documentos-sistema";
import { useIso2700127701DocumentosSistema } from "../hooks/useIso2700127701DocumentosSistema";

type Props = { onBack: () => void };
type ActiveTab = "documentos" | "control";
type DocumentSort = "documento_asc" | "documento_desc" | "revision_desc" | "revision_asc";
type ControlFilters = { search: string; sort: "fecha_desc" | "fecha_asc" };

type DocumentFormState =
  | { open: false }
  | {
      open: true;
      mode: "create" | "edit";
      row: Iso2700127701DocumentoSistema | null;
      value: Iso2700127701DocumentoSistemaPayload;
    };

type ControlFormState =
  | { open: false }
  | {
      open: true;
      mode: "create" | "edit";
      row: Iso2700127701ControlModificacion | null;
      value: Iso2700127701ControlModificacionPayload;
    };

const defaultControlFilters: ControlFilters = { search: "", sort: "fecha_desc" };

function textValue(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return <span className="iso27001-muted-copy">Sin contenido</span>;
  }
  return String(value);
}

function documentPayloadFromRow(row: Iso2700127701DocumentoSistema): Iso2700127701DocumentoSistemaPayload {
  return {
    nivel: row.nivel,
    tipo: row.tipo,
    documento: row.documento,
    modelo_distribucion: row.modelo_distribucion,
    fecha_inicial: row.fecha_inicial,
    fecha_ultima_revision: row.fecha_ultima_revision,
    estado: row.estado,
  };
}

function statusBadge(value: string | null) {
  if (!value?.trim()) return <span className="iso27001-status-badge neutral">Sin definir</span>;
  const normalized = value.trim().toLowerCase();
  const className = normalized.includes("aprob") ? "success" : normalized.includes("pend") ? "warning" : "neutral";
  return <span className={`iso27001-status-badge ${className}`}>{value}</span>;
}

export function Iso2700127701DocumentosSistemaSection({ onBack }: Props) {
  const [activeTab, setActiveTab] = useState<ActiveTab>("documentos");
  const [filters, setFilters] = useState(defaultDocumentosSistemaFilters);
  const [documentSort, setDocumentSort] = useState<DocumentSort>("documento_asc");
  const [controlFilters, setControlFilters] = useState<ControlFilters>(defaultControlFilters);
  const {
    rows,
    controlModificaciones,
    filterOptions,
    meta,
    loading,
    error,
    reload,
  } = useIso2700127701DocumentosSistema(filters);

  const [documentForm, setDocumentForm] = useState<DocumentFormState>({ open: false });
  const [deleteRow, setDeleteRow] = useState<Iso2700127701DocumentoSistema | null>(null);
  const [controlForm, setControlForm] = useState<ControlFormState>({ open: false });
  const [controlDeleteRow, setControlDeleteRow] = useState<Iso2700127701ControlModificacion | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const debouncedControlSearch = useIso2700127701DebouncedValue(controlFilters.search.trim(), 300).toLowerCase();

  const sortedRows = useMemo(() => {
    return [...rows].sort((left, right) => {
      if (documentSort === "documento_asc" || documentSort === "documento_desc") {
        const comparison = left.documento.localeCompare(right.documento, "es", { numeric: true });
        return documentSort === "documento_asc" ? comparison : -comparison;
      }
      const comparison = (left.fecha_ultima_revision ?? "").localeCompare(right.fecha_ultima_revision ?? "");
      return documentSort === "revision_asc" ? comparison : -comparison;
    });
  }, [documentSort, rows]);

  const filteredControlRows = useMemo(() => {
    const term = debouncedControlSearch;
    const filtered = controlModificaciones.filter((row) => (
      !term
      || row.fecha.toLowerCase().includes(term)
      || row.modificaciones_realizadas.toLowerCase().includes(term)
    ));
    return filtered.sort((left, right) => {
      const comparison = left.fecha.localeCompare(right.fecha, "es", { numeric: true });
      return controlFilters.sort === "fecha_asc" ? comparison : -comparison;
    });
  }, [controlFilters.sort, controlModificaciones, debouncedControlSearch]);

  const approvedCount = rows.filter((row) => row.estado?.toLowerCase().includes("aprob")).length;
  const reviewedCount = rows.filter((row) => Boolean(row.fecha_ultima_revision)).length;

  const submitDocument = async () => {
    if (!documentForm.open) return;
    setSubmitting(true);
    setFormError(null);
    try {
      if (documentForm.mode === "create") {
        await createIso2700127701DocumentoSistema(documentForm.value);
      } else if (documentForm.row) {
        await updateIso2700127701DocumentoSistema(documentForm.row.id, documentForm.value);
      }
      setDocumentForm({ open: false });
      await reload();
    } catch {
      setFormError("No se pudo guardar el documento.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitControl = async () => {
    if (!controlForm.open) return;
    setSubmitting(true);
    setFormError(null);
    try {
      if (controlForm.mode === "create") {
        await createIso2700127701DocumentoControlModificacion(controlForm.value);
      } else if (controlForm.row) {
        await updateIso2700127701DocumentoControlModificacion(controlForm.row.id, controlForm.value);
      }
      setControlForm({ open: false });
      await reload();
    } catch {
      setFormError("No se pudo guardar el control documental.");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteDocument = async () => {
    if (!deleteRow) return;
    setSubmitting(true);
    try {
      await deleteIso2700127701DocumentoSistema(deleteRow.id);
      setDeleteRow(null);
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteControl = async () => {
    if (!controlDeleteRow) return;
    setSubmitting(true);
    try {
      await deleteIso2700127701DocumentoControlModificacion(controlDeleteRow.id);
      setControlDeleteRow(null);
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  const columns: Iso2700127701TableColumn<Iso2700127701DocumentoSistema>[] = [
    {
      key: "acciones",
      header: "Acciones",
      className: "iso27001-actions-column",
      render: (row) => (
        <div className="iso27001-row-actions">
          <Iso2700127701IconButton
            label="Editar documento"
            icon={<Edit3 size={15} />}
            onClick={() => {
              setFormError(null);
              setDocumentForm({ open: true, mode: "edit", row, value: documentPayloadFromRow(row) });
            }}
          />
          <Iso2700127701IconButton
            label="Eliminar documento"
            icon={<Trash2 size={15} />}
            danger
            onClick={() => setDeleteRow(row)}
          />
        </div>
      ),
    },
    { key: "nivel", header: "Nivel", className: "iso27001-level-column", render: (row) => textValue(row.nivel) },
    {
      key: "tipo",
      header: "Tipo",
      className: "iso27001-medium-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.tipo ?? undefined}>
          {textValue(row.tipo)}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "documento",
      header: "Documento",
      className: "iso27001-extra-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.documento}>
          {row.documento}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "distribucion",
      header: "Modelo distribución",
      className: "iso27001-medium-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.modelo_distribucion ?? undefined}>
          {textValue(row.modelo_distribucion)}
        </Iso2700127701ScrollableCell>
      ),
    },
    { key: "inicial", header: "Fecha inicial", className: "iso27001-date-column", render: (row) => textValue(row.fecha_inicial) },
    { key: "revision", header: "Última revisión", className: "iso27001-date-column", render: (row) => textValue(row.fecha_ultima_revision) },
    { key: "estado", header: "Estado", className: "iso27001-status-column", render: (row) => statusBadge(row.estado) },
  ];

  return (
    <section className="iso27001-section-shell">
      <Iso2700127701SectionHeader
        eyebrow="Sistema de gestión documental"
        title="Documentos del sistema"
        description="Consulta y mantenimiento del listado documental del SGSI."
        onBack={onBack}
        onRefresh={() => void reload()}
        refreshing={loading}
      />

      <div className="iso27001-overview-grid">
        <Iso2700127701OverviewCard
          icon={Files}
          label="Documentos"
          value={loading ? "..." : meta.total}
        />
        <Iso2700127701OverviewCard
          icon={CheckCircle2}
          label="Aprobados"
          value={loading ? "..." : approvedCount}
        />
        <Iso2700127701OverviewCard
          icon={FileCheck2}
          label="Con última revisión"
          value={loading ? "..." : reviewedCount}
        />
      </div>

      <Iso2700127701Tabs<ActiveTab>
        activeTab={activeTab}
        onChange={setActiveTab}
        ariaLabel="Tablas de documentos del sistema"
        tabs={[
          { id: "documentos", label: "Listado documentos SGSI", count: rows.length },
          { id: "control", label: "Control documental", count: controlModificaciones.length },
        ]}
      />

      {activeTab === "documentos" ? (
        <>
          <Iso2700127701Filters
            title="Filtrar documentos del sistema"
            description="Combina búsqueda, clasificación, fechas y ordenación."
            visibleCount={sortedRows.length}
            totalCount={meta.total}
            onReset={() => {
              setFilters(defaultDocumentosSistemaFilters);
              setDocumentSort("documento_asc");
            }}
          >
            <Iso2700127701InputField
              label="Búsqueda"
              placeholder="Documento, tipo, distribución..."
              value={filters.search}
              onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            />
            <Iso2700127701SelectField
              label="Nivel"
              value={filters.nivel}
              onChange={(event) => setFilters({ ...filters, nivel: event.target.value })}
            >
              <option value="">Todos los niveles</option>
              {filterOptions.niveles.map((nivel) => <option key={nivel} value={nivel}>{nivel}</option>)}
            </Iso2700127701SelectField>
            <Iso2700127701SelectField
              label="Tipo"
              value={filters.tipo}
              onChange={(event) => setFilters({ ...filters, tipo: event.target.value })}
            >
              <option value="">Todos los tipos</option>
              {filterOptions.tipos.map((tipo) => <option key={tipo} value={tipo}>{tipo}</option>)}
            </Iso2700127701SelectField>
            <Iso2700127701SelectField
              label="Modelo de distribución"
              value={filters.modelo_distribucion}
              onChange={(event) => setFilters({ ...filters, modelo_distribucion: event.target.value })}
            >
              <option value="">Todos los modelos</option>
              {filterOptions.modelos_distribucion.map((modelo) => (
                <option key={modelo} value={modelo}>{modelo}</option>
              ))}
            </Iso2700127701SelectField>
            <Iso2700127701SelectField
              label="Estado"
              value={filters.estado}
              onChange={(event) => setFilters({ ...filters, estado: event.target.value })}
            >
              <option value="">Todos los estados</option>
              {filterOptions.estados.map((estado) => <option key={estado} value={estado}>{estado}</option>)}
            </Iso2700127701SelectField>
            <Iso2700127701InputField
              label="Fecha inicial desde"
              type="date"
              value={filters.fecha_inicial_desde}
              onChange={(event) => setFilters({ ...filters, fecha_inicial_desde: event.target.value })}
            />
            <Iso2700127701InputField
              label="Fecha inicial hasta"
              type="date"
              value={filters.fecha_inicial_hasta}
              onChange={(event) => setFilters({ ...filters, fecha_inicial_hasta: event.target.value })}
            />
            <Iso2700127701InputField
              label="Última revisión desde"
              type="date"
              value={filters.fecha_revision_desde}
              onChange={(event) => setFilters({ ...filters, fecha_revision_desde: event.target.value })}
            />
            <Iso2700127701InputField
              label="Última revisión hasta"
              type="date"
              value={filters.fecha_revision_hasta}
              onChange={(event) => setFilters({ ...filters, fecha_revision_hasta: event.target.value })}
            />
            <Iso2700127701SelectField
              label="Ordenación"
              value={documentSort}
              onChange={(event) => setDocumentSort(event.target.value as DocumentSort)}
            >
              <option value="documento_asc">Documento A-Z</option>
              <option value="documento_desc">Documento Z-A</option>
              <option value="revision_desc">Última revisión descendente</option>
              <option value="revision_asc">Última revisión ascendente</option>
            </Iso2700127701SelectField>
          </Iso2700127701Filters>

          <Iso2700127701DataTable
            title="Listado documentos SGSI"
            countLabel={`${sortedRows.length} documentos`}
            rows={sortedRows}
            columns={columns}
            getRowKey={(row) => row.id}
            loading={loading}
            error={error}
            emptyTitle="Sin documentos"
            emptyDescription="No hay documentos que coincidan con los filtros actuales."
            createLabel="Añadir documento"
            onCreate={() => {
              setFormError(null);
              setDocumentForm({ open: true, mode: "create", row: null, value: createEmptyDocumentoSistemaForm() });
            }}
            onRetry={() => void reload()}
          />
        </>
      ) : null}

      {activeTab === "control" ? (
        <>
          <Iso2700127701Filters
            title="Filtrar control documental"
            visibleCount={filteredControlRows.length}
            totalCount={controlModificaciones.length}
            onReset={() => setControlFilters(defaultControlFilters)}
          >
            <Iso2700127701InputField
              label="Búsqueda"
              placeholder="Fecha o modificación..."
              value={controlFilters.search}
              onChange={(event) => setControlFilters({ ...controlFilters, search: event.target.value })}
            />
            <Iso2700127701SelectField
              label="Ordenación"
              value={controlFilters.sort}
              onChange={(event) => setControlFilters({ ...controlFilters, sort: event.target.value as ControlFilters["sort"] })}
            >
              <option value="fecha_desc">Fecha descendente</option>
              <option value="fecha_asc">Fecha ascendente</option>
            </Iso2700127701SelectField>
          </Iso2700127701Filters>

          <Iso2700127701ControlModificacionesTable
            rows={filteredControlRows}
            loading={loading}
            error={error}
            onRetry={() => void reload()}
            onCreate={() => {
              setFormError(null);
              setControlForm({ open: true, mode: "create", row: null, value: createEmptyControlModificacionForm() });
            }}
            onEdit={(row) => {
              setFormError(null);
              setControlForm({
                open: true,
                mode: "edit",
                row,
                value: {
                  fecha: row.fecha,
                  modificaciones_realizadas: row.modificaciones_realizadas,
                },
              });
            }}
            onDelete={setControlDeleteRow}
          />
        </>
      ) : null}

      <DocumentFormModal
        state={documentForm}
        submitting={submitting}
        error={formError}
        onClose={() => setDocumentForm({ open: false })}
        onChange={(value) => documentForm.open && setDocumentForm({ ...documentForm, value })}
        onSubmit={submitDocument}
      />
      <Iso2700127701ControlModificacionFormModal
        open={controlForm.open}
        title={controlForm.open && controlForm.mode === "edit" ? "Editar modificación" : "Nueva modificación"}
        value={controlForm.open ? controlForm.value : createEmptyControlModificacionForm()}
        submitting={submitting}
        error={formError}
        onClose={() => setControlForm({ open: false })}
        onChange={(value) => controlForm.open && setControlForm({ ...controlForm, value })}
        onSubmit={submitControl}
      />
      <Iso2700127701ConfirmDialog
        open={deleteRow !== null}
        title="Eliminar documento"
        description={deleteRow ? `Se eliminará el documento ${deleteRow.documento}.` : ""}
        confirming={submitting}
        onClose={() => setDeleteRow(null)}
        onConfirm={confirmDeleteDocument}
      />
      <Iso2700127701ControlModificacionDeleteModal
        row={controlDeleteRow}
        submitting={submitting}
        onClose={() => setControlDeleteRow(null)}
        onConfirm={confirmDeleteControl}
      />
    </section>
  );
}

type DocumentFormModalProps = {
  state: DocumentFormState;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onChange: (value: Iso2700127701DocumentoSistemaPayload) => void;
  onSubmit: () => void;
};

function DocumentFormModal({
  state,
  submitting,
  error,
  onClose,
  onChange,
  onSubmit,
}: DocumentFormModalProps) {
  const value = state.open ? state.value : createEmptyDocumentoSistemaForm();
  return (
    <Iso2700127701Modal
      open={state.open}
      title={state.open && state.mode === "edit" ? "Editar documento" : "Nuevo documento"}
      description="Completa la clasificación, distribución, fechas y estado del documento SGSI."
      size="wide"
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Iso2700127701Button variant="secondary" onClick={onClose} disabled={submitting}>Cancelar</Iso2700127701Button>
          <Iso2700127701Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar documento"}
          </Iso2700127701Button>
        </>
      }
    >
      <div className="iso27001-form-grid">
        <Iso2700127701InputField
          label="Nivel"
          type="number"
          value={value.nivel ?? ""}
          onChange={(event) => onChange({ ...value, nivel: event.target.value === "" ? null : Number(event.target.value) })}
        />
        <Iso2700127701InputField label="Tipo" value={value.tipo ?? ""} onChange={(event) => onChange({ ...value, tipo: event.target.value })} />
        <Iso2700127701TextareaField label="Documento" value={value.documento} required rows={4} fullWidth onChange={(event) => onChange({ ...value, documento: event.target.value })} />
        <Iso2700127701InputField label="Modelo de distribución" value={value.modelo_distribucion ?? ""} onChange={(event) => onChange({ ...value, modelo_distribucion: event.target.value })} />
        <Iso2700127701InputField label="Estado" value={value.estado ?? ""} onChange={(event) => onChange({ ...value, estado: event.target.value })} />
        <Iso2700127701InputField label="Fecha inicial" type="date" value={value.fecha_inicial ?? ""} onChange={(event) => onChange({ ...value, fecha_inicial: event.target.value || null })} />
        <Iso2700127701InputField label="Última revisión" type="date" value={value.fecha_ultima_revision ?? ""} onChange={(event) => onChange({ ...value, fecha_ultima_revision: event.target.value || null })} />
      </div>
      {error ? <div className="iso27001-form-error">{error}</div> : null}
    </Iso2700127701Modal>
  );
}
